import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Users, Building2, Info } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { buildBreadcrumbLd, buildFaqLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { getRegion } from "../data/regions";
import sponsorImg from "../assets/sponsor-tsv-neu-ulm-spartans.webp";

export default function RegionDetail() {
  const { slug } = useParams();
  const region = getRegion(slug);
  if (!region) return <Navigate to="/einsatzgebiete" replace />;

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Einsatzgebiete", to: "/einsatzgebiete" },
    { label: region.name, to: `/einsatzgebiete/${region.slug}` },
  ];

  const faq = region.faq || [
    {
      q: `Führt Rümpeltaxi Entrümpelungen in ${region.name} durch?`,
      a: `Ja. In der Region ${region.name} führen wir regelmäßig Entrümpelungen, Haushaltsauflösungen und Entsorgungen durch. Rümpeltaxi unterhält dort keine eigene Niederlassung, ist aber regelmäßig im Einsatz.`,
    },
    {
      q: `Wie erhalte ich ein Angebot für ${region.name}?`,
      a: "Schildern Sie uns Ihr Anliegen telefonisch, per WhatsApp oder über das Anfrageformular. Nach Klärung des Umfangs erhalten Sie ein transparentes Angebot.",
    },
  ];
  const body = region.body || [];

  return (
    <div data-testid={`page-region-${region.slug}`}>
      <Seo
        path={`/einsatzgebiete/${region.slug}`}
        title={region.metaTitle}
        description={region.metaDescription}
        jsonLd={[buildBreadcrumbLd(crumbs), buildFaqLd(faq)]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero kicker={`Einsatzgebiet · ${region.name}`} title={region.h1} lead={region.intro} />

      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            {body.length > 0 && (
              <div className="mb-10 space-y-4" data-testid="region-body">
                {body.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-mid-gray">
                    {p}
                  </p>
                ))}
              </div>
            )}
            <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">
              Besonderheiten in {region.name}
            </h2>
            <StaggerGroup className="mt-6 space-y-3">
              {region.highlights.map((h) => (
                <motion.div key={h} variants={itemVariants} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-base text-mid-gray">{h}</span>
                </motion.div>
              ))}
            </StaggerGroup>
            <div className="mt-8 flex items-start gap-3 rounded-sm bg-light-gray p-5">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-mid-gray" />
              <p className="text-sm text-mid-gray">{region.orientation}. Die Angabe dient nur als Orientierung – Einsätze sind auch darüber hinaus möglich.</p>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">Typische Zielgruppen</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-anthracite">
                  {region.audiences.map((a) => <li key={a}>· {a}</li>)}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">Typische Objektarten</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-anthracite">
                  {region.objectTypes.map((o) => <li key={o}>· {o}</li>)}
                </ul>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">Städte &amp; Gemeinden (Auswahl)</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {region.towns.map((t) => (
                  <span key={t} className="rounded-sm bg-light-gray px-3 py-1.5 text-sm text-anthracite">{t}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="grid gap-4">
              <Link
                to="/privatkunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="region-private"
              >
                <span className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Privatkunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/geschaeftskunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="region-business"
              >
                <span className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Geschäftskunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <div className="rounded-sm border border-dashed border-mid-gray/30 bg-white p-6">
                <h3 className="font-heading text-base font-bold text-anthracite">
                  Regionale Referenzen
                </h3>
                <p className="mt-2 text-sm text-mid-gray">
                  Echte Projekte aus {region.name} werden nach Freigabe ergänzt.
                </p>
              </div>
              {region.slug === "ulm-neu-ulm" && (
                <div className="rounded-sm border border-light-gray bg-white p-6" data-testid="region-sponsor">
                  <img
                    src={sponsorImg}
                    alt="Offizieller Sponsor TSV Neu-Ulm Spartans – Rümpeltaxi Altenstadt"
                    className="w-full max-w-[320px] rounded-sm"
                    loading="lazy"
                    decoding="async"
                    data-testid="region-sponsor-image"
                  />
                  <p className="mt-3 text-sm text-mid-gray">
                    In Neu-Ulm sind wir nicht nur beruflich unterwegs: Rümpeltaxi ist offizieller Sponsor der
                    TSV Neu-Ulm Spartans (American Football).
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-light-gray py-16 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <Reveal>
            <span className="kicker">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Fragen zu {region.name}
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <FaqAccordion items={faq} idPrefix={region.slug} />
          </Reveal>
        </div>
      </section>

      <CtaSection title={`Entrümpelung in ${region.name} anfragen`} />
    </div>
  );
}
